import React from 'react'
import { Card, CardContent, Container, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import SvgLightBulb from '../../images/lightbulb.inline.svg'
import SvgNotepad from '../../images/notepad.inline.svg'
import SvgJobDescriptionIcon from '../../images/job-description-icon.inline.svg'

import { BLOCKS } from '@contentful/rich-text-types'
import RichText, { RichTextOptions } from '../RichText'
import { TextInsert as TextInsertType } from '../../types/post'

interface Props {
  textInsert: TextInsertType
  richTextOptions?: RichTextOptions
}

const TextInsert: React.FC<Props> = ({ textInsert, richTextOptions = {} }) => {
  const { classes, cx } = useStyles()

  return (
    <Card
      className={cx({
        [classes.card]: true,
        [classes.cardExpertTip]: textInsert.textType === 'Expert Tip',
        [classes.cardKeyTakeaway]:
          textInsert.textType === 'Key Takeaway' ||
          textInsert.textType === 'Paper',
        [classes.cardBorder]: textInsert.textType === 'Bordered Text',
        [classes.cardJobDescription]: textInsert.textType === 'Job Description',
      })}
      elevation={0}
    >
      <CardContent
        className={cx({
          [classes.cardContent]: true,
          [classes.cardContentBorder]: textInsert.textType === 'Bordered Text',
          [classes.cardContentResume]: textInsert.textType === 'Text Resume',
        })}
      >
        {(textInsert.textType === 'Expert Tip' ||
          textInsert.textType === 'Key Takeaway' ||
          textInsert.textType === 'Job Description') && (
          <Typography
            className={cx({
              [classes.overline]: true,
              [classes.titleKeyTakeaway]:
                textInsert.textType === 'Key Takeaway',
              [classes.titleJobDescription]:
                textInsert.textType === 'Job Description',
            })}
            component="h3"
            variant="overline"
          >
            {textInsert.textType === 'Expert Tip' && (
              <SvgLightBulb className={cx(classes.icon)} />
            )}
            {textInsert.textType === 'Key Takeaway' && (
              <SvgNotepad className={cx(classes.icon, classes.iconNotepad)} />
            )}
            {textInsert.textType === 'Job Description' && (
              <SvgJobDescriptionIcon className={classes.icon} />
            )}
            {textInsert.textType}
          </Typography>
        )}
        <Container disableGutters>
          {textInsert.text && (
            <RichText
              rawBody={textInsert.text.raw}
              options={{
                ...richTextOptions,
                props: {
                  [BLOCKS.HEADING_3]: {
                    className:
                      textInsert.textType === 'Text Resume'
                        ? classes.h3
                        : undefined,
                  },
                  [BLOCKS.UL_LIST]: {
                    className: classes.ul,
                  },
                },
              }}
              references={textInsert.text.references}
            />
          )}
        </Container>
        {textInsert.textType === 'Job Description' && textInsert.source && (
          <Container className={classes.source} disableGutters>
            <RichText rawBody={textInsert.source.raw} />
          </Container>
        )}
      </CardContent>
    </Card>
  )
}

const useStyles = makeStyles()((theme) => ({
  card: {
    margin: '2.5em 0',
  },
  cardBorder: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
  },
  cardExpertTip: {
    backgroundColor: theme.palette.primary.light,
  },
  cardJobDescription: {
    backgroundColor: 'transparent',
    border: `1px dashed ${theme.palette.divider}`,
  },
  cardKeyTakeaway: {
    backgroundColor: theme.palette.background.paper,
  },
  cardContent: {
    padding: '1.1em 2em 1.57em',
    [theme.breakpoints.down('sm')]: {
      padding: '1.1em 1.5em 1.57em',
    },
  },
  cardContentBorder: {
    padding: '3em 2em 3em',
    '&:last-child': {
      paddingBottom: '3em',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '3em 1.5em 3em',
    },
  },
  cardContentResume: {
    padding: '2.93em 2em 3.03em',
    [theme.breakpoints.down('sm')]: {
      padding: '2.93em 1.5em 3.03em',
    },
  },
  h3: {
    borderLeft: `.17em solid ${theme.palette.primary.main}`,
    lineHeight: '1.1em',
    paddingLeft: '.44em',
  },
  icon: {
    fontSize: '1rem',
    height: '1.25em',
    marginRight: '.43em',
    width: '1.25em',
  },
  iconNotepad: {
    height: '1.1em',
  },
  overline: {
    alignItems: 'center',
    display: 'flex',
  },
  source: {
    color: theme.palette.text.secondary,
    fontSize: '.88em',
    marginTop: '.43em',

    '& a': {
      color: theme.palette.text.secondary,
      fontWeight: 800,
    },
  },
  titleKeyTakeaway: {
    color: theme.palette.secondary.dark,
  },
  titleJobDescription: {
    color: theme.palette.text.secondary,
    fontSize: '.81em',
    fontWeight: 400,
  },
  ul: {
    paddingLeft: '1em',
  },
}))

export default TextInsert
